import { useMemo } from "react"

import type {
	TFurniture,
	TGroupedPageData,
	TPageElevationData,
} from "types/elevationReferences.type"

export const useGroupedElevationData = (pages: TPageElevationData[]): TGroupedPageData[] => {
	return useMemo(() => {
		return pages.map((page) => {
			const furnitureByErefId = new Map<number, TFurniture[]>()
			const unassignedFurniture: TFurniture[] = []
			const pageErefIds = new Set(page.erefs.map((eref) => eref.id))

			for (const item of page.furniture) {
				// Treat a nullish eref_id, or one pointing at an eref not on this page,
				// as unassigned. This guarantees no furniture is ever silently dropped
				// from the sidebar (it would otherwise land under an eref key that the
				// `furnitureByErefId.has` filter below discards).
				if (item.eref_id == null || !pageErefIds.has(item.eref_id)) {
					unassignedFurniture.push(item)
				} else {
					const group = furnitureByErefId.get(item.eref_id)

					if (group) {
						group.push(item)
					} else {
						furnitureByErefId.set(item.eref_id, [item])
					}
				}
			}

			// Erefs are sorted by elevation number on the frontend; the furniture inside
			// each eref keeps the backend-provided order (handled server-side).
			const erefs = page.erefs
				.filter((eref) => furnitureByErefId.has(eref.id))
				.map((eref) => ({
					...eref,
					furniture: furnitureByErefId.get(eref.id) ?? [],
				}))
				.sort((a, b) =>
					(a.elevation_number ?? "").localeCompare(b.elevation_number ?? "", undefined, { numeric: true }),
				)

			return {
				page_number: page.page_number,
				page_name: page.page_name,
				erefs,
				unassignedFurniture,
				ecalls: page.ecalls,
			}
		}).filter((page) => page.erefs.length > 0 || page.unassignedFurniture.length > 0)
	}, [pages])
}
